import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Clock, Briefcase, Layers } from 'lucide-react';
import { TimeEntryCalendar, MiniCalendarProps, EntryBlockProps, EntryModalProps, CalendarViewProps } from './interface';
import {
  STATUS_CONFIG, FROZEN_STATUS_CONFIG, CLOSED_STATUS_CONFIG, WIDEST_STATUS_LABEL, HOUR_HEIGHT, DAYS, MONTHS,
  getWeekStart, addDays, formatDateKey, toMinutes, getProjectColor, withAlpha, entryBlockHeight, entryDensity,
} from './utils';

// ─── Mini Calendar Popover ────────────────────────────────────────────────────

function MiniCalendar({ currentWeekStart, onSelectWeek, onClose }: MiniCalendarProps) {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date(currentWeekStart);
    d.setDate(1);
    return d;
  });

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-4 w-72">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setViewMonth(new Date(year, month - 1, 1))}
          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold text-slate-800 dark:text-white">
          {MONTHS[month]} {year}
        </span>
        <button
          onClick={() => setViewMonth(new Date(year, month + 1, 1))}
          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {DAYS.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">
            {d[0]}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((date, i) => {
          if (!date) return <div key={i} />;
          const weekStart = getWeekStart(date);
          const isInCurrentWeek = formatDateKey(weekStart) === formatDateKey(currentWeekStart);
          const isToday = formatDateKey(date) === formatDateKey(today);
          return (
            <button
              key={i}
              onClick={() => { onSelectWeek(weekStart); onClose(); }}
              className={`
                text-xs rounded py-1 w-full text-center transition-colors
                ${isInCurrentWeek
                  ? 'text-white font-semibold'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}
                ${isToday && !isInCurrentWeek ? 'ring-1 ring-indigo-400' : ''}
              `}
              style={isInCurrentWeek ? { background: 'linear-gradient(135deg, #444ab2 0%, #459fec 100%)' } : undefined}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Entry Block ──────────────────────────────────────────────────────────────

function EntryBlock({ entry, color, onClick }: EntryBlockProps) {
  const fromMin = toMinutes(entry.fromHour);
  const toMin   = toMinutes(entry.toHour);
  const top    = (fromMin / 60) * HOUR_HEIGHT;
  const height = entryBlockHeight(fromMin, toMin);
  const projectStatus = entry.project?.status;
  const isInactive = projectStatus === 'FROZEN' || projectStatus === 'CLOSED';
  const statusOverride = projectStatus === 'FROZEN'
    ? FROZEN_STATUS_CONFIG
    : projectStatus === 'CLOSED'
    ? CLOSED_STATUS_CONFIG
    : null;
  const status  = statusOverride ?? STATUS_CONFIG[entry.status];
  const density = entryDensity(height);

  const hoursAndStatus = (
    <div className="mt-auto flex min-w-0 items-center gap-1.5">
      <span className="shrink-0 text-[10px] leading-tight text-slate-500 dark:text-slate-400">{entry.hours}h</span>
      <span className={`truncate rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none ${status.bg} ${status.text}`}>
        {status.label}
      </span>
    </div>
  );

  return (
    <button
      onClick={() => onClick(entry)}
      title={`${entry.project.name}${entry.phase ? ` · ${entry.phase.name}` : ''} · ${entry.hours}h${statusOverride ? ` (${statusOverride.label})` : ''}`}
      className={`absolute left-0.5 right-0.5 rounded-md text-left overflow-hidden transition-all hover:brightness-95 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
        isInactive ? 'opacity-60 grayscale-[40%]' : ''
      }`}
      style={{
        top,
        height,
        backgroundColor: withAlpha(color, isInactive ? 0.08 : 0.12),
        borderLeft: `3px solid ${color}`,
      }}
    >
      <div className="px-1.5 py-1 h-full flex flex-col justify-start gap-0.5 overflow-hidden">
        <p className="text-xs font-semibold leading-tight truncate" style={{ color }}>
          {entry.project.name}
        </p>
        {density === 'compact' && hoursAndStatus}
        {(density === 'standard' || density === 'full') && entry.phase && (
          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate leading-tight">
            {entry.phase.name}
          </p>
        )}
        {density === 'standard' && hoursAndStatus}
        {density === 'full' && (
          <>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">
              {entry.hours}h
            </p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium w-fit mt-auto ${status.bg} ${status.text}`}>
              {status.label}
            </span>
          </>
        )}
      </div>
    </button>
  );
}

// ─── Entry Detail Modal ───────────────────────────────────────────────────────

function EntryModal({ entry, onClose, onEdit }: EntryModalProps) {
  const projectStatus = entry.project?.status;
  const isInactive = projectStatus === 'FROZEN' || projectStatus === 'CLOSED';
  const statusOverride = projectStatus === 'FROZEN'
    ? FROZEN_STATUS_CONFIG
    : projectStatus === 'CLOSED'
    ? CLOSED_STATUS_CONFIG
    : null;
  const status  = statusOverride ?? STATUS_CONFIG[entry.status];
  const canEdit = !isInactive && !!entry.ownerEditable;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 border border-slate-200 dark:border-slate-700">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{entry.project.name}</h2>
            {entry.phase && <p className="text-sm text-slate-500 mt-0.5">{entry.phase.name}</p>}
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
            <X size={18} />
          </button>
        </div>

        <span className={`inline-flex text-xs font-semibold px-3 py-1 rounded-full mb-5 ${status.bg} ${status.text}`}>
          {status.label}
        </span>

        <div className="space-y-3 mt-2">
          <DetailRow icon={<Clock size={14} />} label="Time">
            {entry.fromHour} – {entry.toHour} ({entry.hours}h)
          </DetailRow>
          <DetailRow icon={<Briefcase size={14} />} label="Job Title">
            {entry.jobTitle.name}
          </DetailRow>
          {entry.phase && (
            <DetailRow icon={<Layers size={14} />} label="Phase">
              {entry.phase.name}
            </DetailRow>
          )}
          <DetailRow icon={<span className="text-xs">📅</span>} label="Date">
            {entry.date}
          </DetailRow>
          {entry.notes && (
            <DetailRow icon={<span className="text-xs">📝</span>} label="Notes">
              {entry.notes}
            </DetailRow>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          {canEdit ? (
            <button
              onClick={() => onEdit(entry)}
              className="flex-1 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors"
              style={{ background: 'linear-gradient(135deg, #444ab2 0%, #459fec 100%)' }}
            >
              Edit Entry
            </button>
          ) : (
            <div className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-400 text-sm font-medium py-2.5 rounded-xl text-center cursor-not-allowed">
              {statusOverride ? statusOverride.label : 'View Only'}
            </div>
          )}
          <button
            onClick={onClose}
            className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm font-semibold py-2.5 rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="text-slate-400 w-4 flex-shrink-0 mt-0.5">{icon}</span>
      <span className="text-slate-500 w-20 flex-shrink-0">{label}</span>
      <span className="text-slate-800 dark:text-slate-200 font-medium">{children}</span>
    </div>
  );
}

// ─── Calendar Grid ────────────────────────────────────────────────────────────

export default function CalendarView({
  entries,
  onEditEntry,
  isLoading,
  weekStart,
  onWeekChange,
}: CalendarViewProps) {
  const [showPicker, setShowPicker]       = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<TimeEntryCalendar | null>(null);
  const scrollRef                         = useRef<HTMLDivElement>(null);
  const projectColorIndex                 = useRef(new Map<string, number>());

  // Scroll to 08:00 on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 8 * HOUR_HEIGHT - 16;
    }
  }, []);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const weekLabel = (() => {
    const end = addDays(weekStart, 6);
    const sameMonth = weekStart.getMonth() === end.getMonth();
    if (sameMonth) {
      return `${MONTHS[weekStart.getMonth()]} ${weekStart.getDate()}–${end.getDate()}, ${weekStart.getFullYear()}`;
    }
    return `${MONTHS[weekStart.getMonth()]} ${weekStart.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${weekStart.getFullYear()}`;
  })();

  const entriesByDay = weekDays.reduce<Record<string, TimeEntryCalendar[]>>((acc, day) => {
    const key = formatDateKey(day);
    acc[key] = entries.filter((e) => e.date === key);
    return acc;
  }, {});

  const today = formatDateKey(new Date());

  return (
    <div className="flex flex-col bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>

      {/* ── Week navigator ── */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
        <button
          onClick={() => onWeekChange(addDays(weekStart, -7))}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
        >
          <ChevronLeft size={16} />
        </button>

        <div className="relative">
          <button
            onClick={() => setShowPicker((v) => !v)}
            className="text-sm font-semibold text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            {weekLabel}
          </button>
          {showPicker && (
            <MiniCalendar
              currentWeekStart={weekStart}
              onSelectWeek={onWeekChange}
              onClose={() => setShowPicker(false)}
            />
          )}
        </div>

        <button
          onClick={() => onWeekChange(addDays(weekStart, 7))}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* ── Day column headers ── */}
      <div className="flex flex-shrink-0 border-b border-slate-100 dark:border-slate-800">
        <div className="w-12 flex-shrink-0" />
        {weekDays.map((day) => {
          const key     = formatDateKey(day);
          const isToday = key === today;
          return (
            <div
              key={key}
              className={`flex-1 text-center py-2.5 border-l border-slate-100 dark:border-slate-800 ${isToday ? 'bg-indigo-50 dark:bg-indigo-950/30' : ''}`}
            >
              <p className={`text-[11px] font-medium uppercase tracking-wide ${isToday ? 'text-indigo-500' : 'text-slate-400'}`}>
                {DAYS[day.getDay()]}
              </p>
              <p className={`text-sm font-bold mt-0.5 ${isToday ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-300'}`}>
                {day.getDate()}
              </p>
            </div>
          );
        })}
      </div>

      {/* ── Scrollable 24h grid ── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-slate-900/60">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        )}

        <div className="flex" style={{ height: 24 * HOUR_HEIGHT }}>
          {/* Hour labels */}
          <div className="w-12 flex-shrink-0 relative">
            {Array.from({ length: 24 }, (_, h) => (
              <div
                key={h}
                className="absolute w-full flex items-start justify-end pr-2"
                style={{ top: h * HOUR_HEIGHT, height: HOUR_HEIGHT }}
              >
                <span className="text-[10px] text-slate-400 -translate-y-2">
                  {String(h).padStart(2, '0')}:00
                </span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day) => {
            const key      = formatDateKey(day);
            const isToday  = key === today;
            const dayEntries = entriesByDay[key] ?? [];

            return (
              <div
                key={key}
                className={`flex-1 border-l border-slate-100 dark:border-slate-800 relative ${isToday ? 'bg-indigo-50/20 dark:bg-indigo-950/10' : ''}`}
                style={{ height: 24 * HOUR_HEIGHT }}
              >
                {/* Hour grid lines */}
                {Array.from({ length: 24 }, (_, h) => (
                  <div
                    key={h}
                    className="absolute w-full border-t border-slate-100 dark:border-slate-800"
                    style={{ top: h * HOUR_HEIGHT }}
                  />
                ))}
                {/* Half-hour lines */}
                {Array.from({ length: 24 }, (_, h) => (
                  <div
                    key={`h-${h}`}
                    className="absolute w-full border-t border-slate-50 dark:border-slate-800/50"
                    style={{ top: h * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
                  />
                ))}

                {/* Entry blocks */}
                {dayEntries.map((entry) => (
                  <EntryBlock
                    key={entry.id}
                    entry={entry}
                    color={getProjectColor(entry.project.id, projectColorIndex.current)}
                    onClick={setSelectedEntry}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Entry Modal ── */}
      {selectedEntry && (
        <EntryModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onEdit={(entry) => { setSelectedEntry(null); onEditEntry(entry); }}
        />
      )}
    </div>
  );
}

export { STATUS_CONFIG, FROZEN_STATUS_CONFIG, CLOSED_STATUS_CONFIG, WIDEST_STATUS_LABEL, getWeekStart, formatDateKey };
export type { TimeEntryCalendar };
